import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import AcupunctureCardService from "../services/AcupunctureCardService";
import PatientService from "../services/PatientService";

const BEIGE = "#ddc9ae";
const GOLD = "#B8860B";

const styles = {
  page: { padding: "30px", backgroundColor: "#f8f9fa", minHeight: "100vh" },
  card: { maxWidth: "950px", margin: "0 auto", backgroundColor: "#fff", border: "1px solid #000" },
  headerBanner: { backgroundColor: BEIGE, padding: "25px 20px", textAlign: "center" },
  sectionHeader: { backgroundColor: BEIGE, padding: "8px 10px", fontWeight: "bold", textAlign: "center", borderTop: "1px solid #000", borderBottom: "1px solid #000" },
  subHeader: { backgroundColor: BEIGE, padding: "6px 10px", fontWeight: "bold", textAlign: "center", borderBottom: "1px solid #000" },
  row: { display: "flex", borderBottom: "1px solid #000" },
  cell: { flex: 1, padding: "8px 12px", borderRight: "1px solid #000" },
  cellLast: { flex: 1, padding: "8px 12px" },
  fieldLabel: { fontSize: "0.85rem", color: "#555", marginBottom: "4px", display: "block" },
  input: { width: "100%", border: "none", borderBottom: "1px solid #ccc", outline: "none", padding: "2px 0" },
  textarea: { width: "100%", minHeight: "80px", border: "1px solid #ccc", padding: "6px", outline: "none" },
};

const pill = (active) => ({
  display: "inline-block",
  padding: "4px 12px",
  margin: "3px",
  borderRadius: "14px",
  border: `1px solid ${GOLD}`,
  backgroundColor: active ? GOLD : "#fff",
  color: active ? "#fff" : "#333",
  cursor: "pointer",
  fontSize: "0.85rem",
  userSelect: "none",
});

function RadioGroup({ label, value, options, onChange }) {
  return (
    <div style={{ marginBottom: "10px" }}>
      {label && <span style={styles.fieldLabel}>{label}</span>}
      {options.map((opt) => (
        <span key={opt} style={pill(value === opt)} onClick={() => onChange(value === opt ? null : opt)}>
          {opt}
        </span>
      ))}
    </div>
  );
}

function CheckboxGroup({ label, values, options, onChange }) {
  const list = values || [];
  const toggle = (opt) => {
    if (list.includes(opt)) onChange(list.filter((v) => v !== opt));
    else onChange([...list, opt]);
  };
  return (
    <div style={{ marginBottom: "10px" }}>
      {label && <span style={styles.fieldLabel}>{label}</span>}
      {options.map((opt) => (
        <span key={opt} style={pill(list.includes(opt))} onClick={() => toggle(opt)}>
          {opt}
        </span>
      ))}
    </div>
  );
}

function BoolField({ label, value, onChange }) {
  return <RadioGroup label={label} value={value === true ? "Da" : value === false ? "Ne" : null}
    options={["Da", "Ne"]}
    onChange={(v) => onChange(v === "Da" ? true : v === "Ne" ? false : null)} />;
}

const emptyCard = {
  patientDTO: null,
  examDate: "", workplace: "", occupation: "", address: "",
  allopathicDiagnosis: "", traditionalDiagnosis: "",
  personalHistory: "", familyHistory: "",
  painAreas: [], bloodPressure: null, sleep: null, dreams: null,
  dizziness: null, heartPalpitations: null, sweating: null, sweatingTime: null,
  urinationColor: null, urinationFrequency: null, stool: null, prefers: null, aversion: null,
  edema: null, hearing: null, eyes: [], menstruation: null, pulseDepth: null, pulseSpeed: null,
  cough: null, expectoration: null, tinnitus: null, appetite: null, vomiting: null,
  bloodColor: null, bloodClots: null, tongueColor: null, tongueCoating: null,
  backShuSensitive: null, backShuWhich: "", muFrontSensitive: null, muFrontWhich: "",
  skin: null, hair: [], nails: null,
  exogenousPathogenicFactors: [], endogenousPathogenicFactors: [],
  exteriorInterior: null, coldHeat: null, excessDeficiency: null, yinYang: null,
  affectedMeridians: [], therapyMethods: [], zangFuOrgansAffected: null,
  therapyPrinciple: "", pointSelection: "", sessions: [],
};

export default function AcupunctureCardForm() {
  const { patientID, cardID } = useParams();
  const navigate = useNavigate();
  const pdfRef = useRef();
  const [patient, setPatient] = useState(null);
  const [card, setCard] = useState(emptyCard);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(cardID);

  useEffect(() => {
    if (isEdit) {
      AcupunctureCardService.getById(cardID).then((data) => {
        setCard({ ...emptyCard, ...data });
        setPatient(data.patientDTO);
        setLoading(false);
      });
    } else {
      PatientService.getPatientById(patientID).then((p) => {
        setPatient(p);
        setCard({ ...emptyCard, patientDTO: p });
        setLoading(false);
      });
    }
  }, [cardID, patientID, isEdit]);

  const set = (key) => (value) => setCard((prev) => ({ ...prev, [key]: value }));
  const setInput = (key) => (e) => setCard((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...card, patientDTO: patient };
      if (isEdit) {
        await AcupunctureCardService.update(cardID, payload);
      } else {
        const created = await AcupunctureCardService.create(payload);
        navigate(`/acupuncture-cards/${created.cardID}/edit`, { replace: true });
      }
      alert("Karton je sačuvan.");
    } catch (err) {
      console.error(err);
      alert("Greška pri čuvanju kartona: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateSessionField = (visitId, index, key, value) => {
    setCard((prev) => {
      const sessions = [...prev.sessions];
      sessions[index] = { ...sessions[index], [key]: value };
      return { ...prev, sessions };
    });
  };

  const saveSessionField = (session) => {
    AcupunctureCardService.updateSession(session.sessionID, {
      therapy: session.therapy,
      patientState: session.patientState,
    }).catch((err) => {
      console.error(err);
      alert("Greška pri čuvanju sesije: " + err.message);
    });
  };

  const downloadPDF = async () => {
    const element = pdfRef.current;
    const buttons = document.querySelectorAll(".no-pdf-tools");
    buttons.forEach((b) => (b.style.display = "none"));

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Karton_akupunktura_${patient?.name || ""}_${patient?.surname || ""}.pdf`);
    } catch (error) {
      console.error("Greška pri generisanju PDF-a:", error);
    } finally {
      buttons.forEach((b) => (b.style.display = ""));
    }
  };

  if (loading) return <div style={{ padding: "40px", textAlign: "center" }}>Učitavanje...</div>;

  return (
    <div style={styles.page}>
      <div className="no-pdf-tools" style={{ maxWidth: "950px", margin: "0 auto 15px auto", display: "flex", justifyContent: "space-between" }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Nazad</button>
        <div>
          <button className="btn btn-success me-2" onClick={handleSave} disabled={saving}>
            {saving ? "Čuvanje..." : "💾 Sačuvaj"}
          </button>
          <button className="btn btn-warning" onClick={downloadPDF}>📄 Preuzmi PDF</button>
        </div>
      </div>

      <div ref={pdfRef} style={styles.card}>
        {/* NASLOV */}
        <div style={styles.headerBanner}>
          <h2 style={{ margin: 0 }}>Karton za vodjenje pacijenta koji se leči</h2>
          <div style={{ fontFamily: "cursive", fontSize: "1.8rem", marginTop: "5px" }}>akupunkturom</div>
        </div>

        {/* OSNOVNI PODACI */}
        <div style={styles.row}>
          <div style={styles.cell}>
            <span style={styles.fieldLabel}>Broj kartona</span>
            <div>{isEdit ? card.cardID : "biće dodeljen nakon čuvanja"}</div>
          </div>
          <div style={styles.cellLast}>
            <span style={styles.fieldLabel}>Datum pregleda</span>
            <input type="date" style={styles.input} value={card.examDate || ""} onChange={setInput("examDate")} />
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.cell}>
            <span style={styles.fieldLabel}>Ime i prezime</span>
            <div>{patient ? `${patient.name} ${patient.surname}` : "/"}</div>
          </div>
          <div style={styles.cellLast}>
            <span style={styles.fieldLabel}>Telefon</span>
            <div>{patient?.phone || "/"}</div>
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.cell}>
            <span style={styles.fieldLabel}>Datum rodjenja</span>
            <div>{patient?.birthDate ? String(patient.birthDate).split("T")[0] : "/"}</div>
          </div>
          <div style={styles.cellLast}>
            <span style={styles.fieldLabel}>Radno mesto</span>
            <input style={styles.input} value={card.workplace || ""} onChange={setInput("workplace")} />
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.cell}>
            <span style={styles.fieldLabel}>Zanimanje</span>
            <input style={styles.input} value={card.occupation || ""} onChange={setInput("occupation")} />
          </div>
          <div style={styles.cellLast}>
            <span style={styles.fieldLabel}>Adresa</span>
            <input style={styles.input} value={card.address || ""} onChange={setInput("address")} />
          </div>
        </div>
        <div style={{ ...styles.row, borderBottom: "none" }}>
          <div style={styles.cell}>
            <span style={styles.fieldLabel}>Alergije</span>
            <div>{patient?.allergies || "Nema"}</div>
          </div>
          <div style={styles.cellLast}>
            <span style={styles.fieldLabel}>Operacije</span>
            <div>{patient?.surgeries || "/"}</div>
          </div>
        </div>
        <div style={{ ...styles.row, borderBottom: "1px solid #000" }}>
          <div style={{ ...styles.cellLast, flex: "0 0 100%" }}>
            <span style={styles.fieldLabel}>Hronično</span>
            <div>{patient?.otherInfo || "/"}</div>
          </div>
        </div>

        {/* DIJAGNOZA */}
        <div style={styles.sectionHeader}>DIJAGNOZA</div>
        <div style={{ display: "flex", borderBottom: "1px solid #000" }}>
          <div style={{ flex: 1, borderRight: "1px solid #000" }}>
            <div style={styles.subHeader}>Alopatska</div>
            <div style={{ padding: "10px" }}>
              <textarea style={styles.textarea} value={card.allopathicDiagnosis || ""} onChange={setInput("allopathicDiagnosis")} />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={styles.subHeader}>Tradicionalna</div>
            <div style={{ padding: "10px" }}>
              <div style={{ display: "flex", justifyContent: "center", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                <span style={{ ...pill(true), backgroundColor: "#c0392b", borderColor: "#c0392b" }}>VATRA — srce, tanko crevo</span>
                <span style={{ ...pill(true), backgroundColor: "#27ae60", borderColor: "#27ae60" }}>DRVO — jetra, žuč</span>
                <span style={{ ...pill(true), backgroundColor: "#d4ac0d", borderColor: "#d4ac0d" }}>ZEMLJA — slezina, gušterača, želudac</span>
                <span style={{ ...pill(true), backgroundColor: "#7f8c8d", borderColor: "#7f8c8d" }}>METAL — pluća, debelo crevo</span>
                <span style={{ ...pill(true), backgroundColor: "#2980b9", borderColor: "#2980b9" }}>VODA — bubreg, mokraćna bešika</span>
              </div>
              <textarea style={styles.textarea} value={card.traditionalDiagnosis || ""} onChange={setInput("traditionalDiagnosis")} />
            </div>
          </div>
        </div>

        {/* KRATKA ANAMNEZA */}
        <div style={styles.sectionHeader}>KRATKA ANAMNEZA</div>
        <div style={styles.subHeader}>Lična-status</div>
        <div style={{ padding: "10px", borderBottom: "1px solid #000" }}>
          <textarea style={styles.textarea} value={card.personalHistory || ""} onChange={setInput("personalHistory")} />
        </div>
        <div style={styles.subHeader}>Porodična</div>
        <div style={{ padding: "10px" }}>
          <textarea style={styles.textarea} value={card.familyHistory || ""} onChange={setInput("familyHistory")} />
        </div>

        {/* SIMPTOMI */}
        <div style={styles.sectionHeader}>SIMPTOMI</div>
        <div style={{ padding: "15px" }}>
          <CheckboxGroup label="Bolovi" values={card.painAreas} onChange={set("painAreas")}
            options={["Glava", "Vrat", "Telo", "Mišići", "Udovi", "Kosti"]} />
          <RadioGroup label="Krvni pritisak" value={card.bloodPressure} onChange={set("bloodPressure")}
            options={["Visok", "Normalan", "Nizak"]} />
          <RadioGroup label="Spavanje" value={card.sleep} onChange={set("sleep")} options={["Dobro", "Loše"]} />
          <RadioGroup label="Snovi" value={card.dreams} onChange={set("dreams")} options={["Prijatni", "Neprijatni"]} />
          <BoolField label="Vrtoglavica" value={card.dizziness} onChange={set("dizziness")} />
          <BoolField label="Srčane palpitacije" value={card.heartPalpitations} onChange={set("heartPalpitations")} />
          <BoolField label="Znojenje" value={card.sweating} onChange={set("sweating")} />
          <RadioGroup label="Znojenje - vreme" value={card.sweatingTime} onChange={set("sweatingTime")} options={["Noću", "Danju"]} />
          <RadioGroup label="Uriniranje - boja" value={card.urinationColor} onChange={set("urinationColor")} options={["Svetlo", "Tamna"]} />
          <RadioGroup label="Uriniranje - učestalost" value={card.urinationFrequency} onChange={set("urinationFrequency")} options={["Često", "Retko"]} />
          <RadioGroup label="Stolica" value={card.stool} onChange={set("stool")} options={["Meka", "Tvrda", "BP"]} />
          <RadioGroup label="Preferira" value={card.prefers} onChange={set("prefers")} options={["Hladno", "Toplo"]} />
          <RadioGroup label="Averzija" value={card.aversion} onChange={set("aversion")} options={["Na hladno", "Na toplo"]} />
          <BoolField label="Edemi" value={card.edema} onChange={set("edema")} />
          <RadioGroup label="Sluh" value={card.hearing} onChange={set("hearing")} options={["Dobar", "Slab"]} />
          <CheckboxGroup label="Oči" values={card.eyes} onChange={set("eyes")} options={["Crvene", "Suve", "BP"]} />
          <RadioGroup label="Menstruacija" value={card.menstruation} onChange={set("menstruation")} options={["Redovna", "Neredovna", "BP"]} />
          <RadioGroup label="Puls - dubina" value={card.pulseDepth} onChange={set("pulseDepth")} options={["Dubok", "Površinski"]} />
          <RadioGroup label="Puls - brzina" value={card.pulseSpeed} onChange={set("pulseSpeed")} options={["Spor", "Brz"]} />
          <BoolField label="Kašalj" value={card.cough} onChange={set("cough")} />
          <BoolField label="Iskašljavanje" value={card.expectoration} onChange={set("expectoration")} />
          <BoolField label="Zvonjenje u ušima" value={card.tinnitus} onChange={set("tinnitus")} />
          <BoolField label="Apetit" value={card.appetite} onChange={set("appetite")} />
          <BoolField label="Povraćanje" value={card.vomiting} onChange={set("vomiting")} />
          <RadioGroup label="Krv - boja" value={card.bloodColor} onChange={set("bloodColor")} options={["Svetla", "Tamna"]} />
          <BoolField label="Ugrušci" value={card.bloodClots} onChange={set("bloodClots")} />
          <RadioGroup label="Jezik - boja" value={card.tongueColor} onChange={set("tongueColor")} options={["Crven", "Bled"]} />
          <RadioGroup label="Jezik - obloga" value={card.tongueCoating} onChange={set("tongueCoating")} options={["Obložen", "Neobložen"]} />
          <BoolField label="Back Shu (palpatorno osetljive)" value={card.backShuSensitive} onChange={set("backShuSensitive")} />
          <div style={{ marginBottom: "10px" }}>
            <span style={styles.fieldLabel}>Ako da, koje:</span>
            <input style={styles.input} value={card.backShuWhich || ""} onChange={setInput("backShuWhich")} />
          </div>
          <BoolField label="Mu Front (palpatorno osetljive)" value={card.muFrontSensitive} onChange={set("muFrontSensitive")} />
          <div style={{ marginBottom: "10px" }}>
            <span style={styles.fieldLabel}>Ako da, koje:</span>
            <input style={styles.input} value={card.muFrontWhich || ""} onChange={setInput("muFrontWhich")} />
          </div>
          <RadioGroup label="Koža" value={card.skin} onChange={set("skin")} options={["Suva", "Crvena", "BP"]} />
          <CheckboxGroup label="Kosa" values={card.hair} onChange={set("hair")} options={["Suva", "Masna", "Seda", "Retka", "Gusta"]} />
          <RadioGroup label="Nokti" value={card.nails} onChange={set("nails")} options={["Deformisani", "BP"]} />
        </div>

        {/* TCM KATEGORIJE */}
        <div style={styles.sectionHeader}>TCM KATEGORIJE</div>
        <div style={{ padding: "15px" }}>
          <CheckboxGroup label="Egzogeni patogeni faktori" values={card.exogenousPathogenicFactors} onChange={set("exogenousPathogenicFactors")}
            options={["Vetar", "Hladnoća", "Toplota", "Suvoća", "Vlaga"]} />
          <CheckboxGroup label="Endogeni patogeni faktori" values={card.endogenousPathogenicFactors} onChange={set("endogenousPathogenicFactors")}
            options={["Briga", "Strepnja", "Ljutnja", "Depresija", "Radost", "Žalost", "Strah"]} />
          <RadioGroup label="Spoljno / Unutrašnje" value={card.exteriorInterior} onChange={set("exteriorInterior")} options={["Spoljno", "Unutrašnje"]} />
          <RadioGroup label="Hladno / Toplo" value={card.coldHeat} onChange={set("coldHeat")} options={["Hladno", "Toplo"]} />
          <RadioGroup label="Ekces / Deficit" value={card.excessDeficiency} onChange={set("excessDeficiency")} options={["Ekces", "Deficit"]} />
          <RadioGroup label="Yin / Yang" value={card.yinYang} onChange={set("yinYang")} options={["Yin", "Yang"]} />
          <CheckboxGroup label="Zahvaćeni meridijani" values={card.affectedMeridians} onChange={set("affectedMeridians")}
            options={["LU", "LI", "ST", "SP", "HT", "SI", "BL", "KI", "PC", "SJ", "GB", "LR"]} />
          <CheckboxGroup label="Izabrane metode terapije" values={card.therapyMethods} onChange={set("therapyMethods")}
            options={["Akupunktura", "Moksa", "Ventuze", "Elektroakupunktura", "Masaža"]} />
          <RadioGroup label="Zahvaćeni Zang Fu organi" value={card.zangFuOrgansAffected === true ? "DA" : card.zangFuOrgansAffected === false ? "NE" : null}
            onChange={(v) => set("zangFuOrgansAffected")(v === "DA" ? true : v === "NE" ? false : null)}
            options={["DA", "NE"]} />
        </div>

        {/* PRINCIP TERAPIJE / IZBOR TAČAKA */}
        <div style={{ display: "flex", borderTop: "1px solid #000" }}>
          <div style={{ flex: 1, borderRight: "1px solid #000" }}>
            <div style={styles.sectionHeader}>PRINCIP TERAPIJE</div>
            <div style={{ padding: "10px" }}>
              <textarea style={styles.textarea} value={card.therapyPrinciple || ""} onChange={setInput("therapyPrinciple")} />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={styles.sectionHeader}>IZBOR TAČAKA</div>
            <div style={{ padding: "10px" }}>
              <textarea style={styles.textarea} value={card.pointSelection || ""} onChange={setInput("pointSelection")} />
            </div>
          </div>
        </div>

        {/* SESIJE TERAPIJE - preuzeto iz poseta (Visit) povezanih sa ovim kartonom */}
        <div style={{ borderTop: "1px solid #000" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...styles.sectionHeader, width: "60px" }}>BR.</th>
                <th style={{ ...styles.sectionHeader, width: "120px" }}>DATUM</th>
                <th style={styles.sectionHeader}>TERAPIJA</th>
                <th style={styles.sectionHeader}>STANJE PACIJENTA</th>
              </tr>
            </thead>
            <tbody>
              {(card.sessions || []).length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ border: "1px solid #000", textAlign: "center", padding: "10px", color: "#777" }}>
                    Nema evidentiranih poseta za ovaj karton.
                  </td>
                </tr>
              ) : (
                card.sessions.map((s, i) => (
                  <tr key={s.sessionID ?? i}>
                    <td style={{ border: "1px solid #000", textAlign: "center", padding: "6px" }}>{i + 1}.</td>
                    <td style={{ border: "1px solid #000", padding: "6px" }}>{s.sessionDate || "/"}</td>
                    <td style={{ border: "1px solid #000", padding: "4px" }}>
                      <textarea
                        style={{ ...styles.input, resize: "vertical", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}
                        rows={2}
                        value={s.therapy || ""}
                        onChange={(e) => updateSessionField(s.sessionID, i, "therapy", e.target.value)}
                        onBlur={() => saveSessionField(s)}
                      />
                    </td>
                    <td style={{ border: "1px solid #000", padding: "4px" }}>
                      <textarea
                        style={{ ...styles.input, resize: "vertical", overflowWrap: "break-word", whiteSpace: "pre-wrap" }}
                        rows={2}
                        value={s.patientState || ""}
                        onChange={(e) => updateSessionField(s.sessionID, i, "patientState", e.target.value)}
                        onBlur={() => saveSessionField(s)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="no-pdf-tools" style={{ padding: "10px", fontSize: "0.85rem", color: "#777" }}>
            Terapija i stanje pacijenta se čuvaju automatski kad izađete iz polja. Nove sesije (redovi) se dodaju kreiranjem posete "Telesna akupunktura" i povezivanjem sa ovim kartonom u formi Nova poseta.
          </div>
        </div>
      </div>

      <div className="no-pdf-tools" style={{ maxWidth: "950px", margin: "15px auto 0 auto", display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-success" onClick={handleSave} disabled={saving}>
          {saving ? "Čuvanje..." : "💾 Sačuvaj"}
        </button>
      </div>
    </div>
  );
}
